import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNotifier } from "react-headless-notifier";
import {
  Modal,
  Button,
  Input,
  Label,
  FormGroup,
  NotifierNotification,
} from "@mwi/ui";
import { useUpdateUserMutation } from "api";
import useErrorHandler from "hooks/useErrorHandler";

type Inputs = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
};

const UpdateAdminModal = ({
  isOpen,
  onClose,
  user,
}: {
  isOpen: boolean;
  onClose: () => void;
  user: Inputs;
}): JSX.Element => {
  const { notify } = useNotifier();
  const errorHandler = useErrorHandler();

  const {
    register,
    handleSubmit,
    reset,
    setError,
    setValue,
    formState: { errors },
  } = useForm<Inputs>();

  const [updateUser, { data: updatedUser, isLoading, error }] =
    useUpdateUserMutation();

  useEffect(() => {
    if (user) {
      setValue("id", user.id);
      setValue("first_name", user.first_name);
      setValue("last_name", user.last_name);
      setValue("email", user.email);
    }
  }, [user]);

  useEffect(() => {
    if (error) {
      errorHandler(error, setError);
    }
  }, [error]);

  useEffect(() => {
    if (updatedUser) {
      handleClose();

      notify(
        <NotifierNotification
          title="User Updated"
          message="User successfully updated"
        />,
      );
    }
  }, [updatedUser]);

  const handleUpdate = (data) => {
    updateUser({
      id: data.id,
      body: data,
    });
  };

  const handleClose = () => {
    onClose();
    reset();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      className="!max-w-[760px] w-full"
    >
      <Modal.Header title="Update Account" />

      <form onSubmit={handleSubmit(handleUpdate)}>
        <Modal.Content>
          <FormGroup error={errors.first_name?.message}>
            <Label>First Name</Label>
            <Input {...register("first_name")} />
          </FormGroup>

          <FormGroup error={errors.last_name?.message}>
            <Label>Last Name</Label>
            <Input {...register("last_name")} />
          </FormGroup>

          <FormGroup error={errors.email?.message}>
            <Label>Email</Label>
            <Input {...register("email")} />
          </FormGroup>
        </Modal.Content>

        <Modal.Footer>
          <div className="flex flex-wrap justify-end gap-4">
            <Button onClick={handleClose} variant="outlined">
              Cancel
            </Button>

            <div className="mobile:max-w-[200px] w-full">
              <Button type="submit" disabled={isLoading} block>
                Update
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default UpdateAdminModal;
